import { useState } from 'react';
import { Download } from 'lucide-react';
import { exportDataSubset } from '../utils/dataProcessor';

/**
 * Reusable Download Data button for any section/subsection.
 * Exports the provided data subset to XLSX.
 *
 * @param {Object} props
 * @param {Array} props.data - The data subset to export (array of result objects)
 * @param {string} props.filename - Filename for the download (without extension)
 * @param {string} props.label - Display label for the button (default: "Download data")
 * @param {string} [props.subsetLabel] - Label for the sheet tab in the XLSX
 * @param {string} [props.className] - Additional CSS classes
 * @param {boolean} [props.compact] - Use compact (inline) style
 */
export default function DownloadDataButton({
  data,
  filename,
  label = 'Download data',
  subsetLabel,
  className = '',
  compact = false,
}) {
  const [exporting, setExporting] = useState(false);

  const handleDownload = async () => {
    if (!data || data.length === 0) return;
    setExporting(true);
    try {
      await exportDataSubset(data, filename, subsetLabel);
    } catch (err) {
      console.error('Failed to export data:', err);
    } finally {
      setExporting(false);
    }
  };

  if (!data || data.length === 0) return null;

  if (compact) {
    return (
      <button
        onClick={handleDownload}
        disabled={exporting}
        className={`flex items-center gap-1 px-2 py-1 text-xs text-gray-500 hover:text-[#006837] hover:bg-green-50 rounded transition-colors disabled:opacity-50 ${className}`}
        title={`Download ${data.length} results as .xlsx`}
      >
        <Download className="w-3.5 h-3.5" />
        {exporting ? 'Exporting...' : label}
      </button>
    );
  }

  return (
    <button
      onClick={handleDownload}
      disabled={exporting}
      className={`flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 hover:text-[#006837] hover:bg-green-50 border border-gray-200 rounded-lg transition-colors disabled:opacity-50 ${className}`}
      title={`Download ${data.length} results as .xlsx`}
    >
      <Download className="w-4 h-4" />
      {exporting ? 'Exporting...' : `${label} (${data.length})`}
    </button>
  );
}
